import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Crown, LogOut, Clock } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { useDeadlineCountdown } from "@/components/Countdown";

// Pages where a logged-in user must still be able to reach checkout.
const ALLOW = ["/subscribe", "/payment/callback"];

// Handles the end of the free welcome window for signed-in, non-paying users:
//  • fires a one-time toast when under 1 hour remains, and
//  • shows a full-screen "free access ended" gate once it runs out.
export const TrialGate = () => {
  const { t } = useTranslation();
  const nav = useNavigate();
  const loc = useLocation();
  const { user, paid, accessUntil, logout } = useAuth();
  const { fromDisplay, freeAccess } = useCurrency();

  const active = !freeAccess && Boolean(user) && !paid && !!accessUntil;
  const { ms, expired } = useDeadlineCountdown(active ? accessUntil : null);

  // One-time last-hour reminder.
  useEffect(() => {
    if (!active || expired) return;
    if (ms > 0 && ms <= 3600000) {
      const key = `ntv_lasthour_${user.id}`;
      if (localStorage.getItem(key) !== "1") {
        localStorage.setItem(key, "1");
        toast.warning(t("promo.lastHourTitle"), {
          description: t("promo.lastHourBody"),
          duration: 12000,
          action: { label: t("promo.pickPlan"), onClick: () => nav("/subscribe") },
        });
      }
    }
  }, [active, expired, ms, user, t, nav]);

  if (!active || !expired) return null;
  if (ALLOW.some((p) => loc.pathname === p || loc.pathname.startsWith(p + "/"))) return null;

  return (
    <div data-testid="expiry-gate"
      className="fixed inset-0 z-[80] grid place-items-center p-4 bg-black/90 backdrop-blur-md animate-fade-in">
      <div className="relative w-full max-w-md rounded-2xl bg-[#121216] border border-gold/30 shadow-2xl p-7 sm:p-9 text-center animate-fade-up">
        <div className="mx-auto mb-5 w-16 h-16 rounded-full bg-gradient-to-br from-gold to-amber-500 grid place-items-center shadow-lg shadow-gold/30">
          <Clock size={30} className="text-black" />
        </div>
        <h2 className="font-head text-2xl sm:text-3xl font-extrabold tracking-tight mb-3">{t("promo.expiredTitle")}</h2>
        <p className="text-zinc-400 text-sm leading-relaxed mb-6">{t("promo.expiredBody")}</p>
        <button data-testid="expiry-gate-cta" onClick={() => nav("/subscribe")}
          className="flex items-center justify-center gap-2 w-full bg-gradient-to-r from-gold to-amber-500 text-black font-bold py-3.5 rounded-xl hover:brightness-110 transition shadow-lg shadow-gold/20">
          <Crown size={18} /> {t("promo.pickPlan")}
        </button>
        <p className="mt-3 text-xs text-zinc-500">{t("promo.expiredFrom", { price: fromDisplay || "R19.99" })}</p>
        <button data-testid="expiry-gate-logout" onClick={logout}
          className="mt-5 inline-flex items-center gap-1.5 text-zinc-500 hover:text-zinc-300 text-sm transition-colors">
          <LogOut size={14} /> {t("nav.logout")}
        </button>
      </div>
    </div>
  );
};
